import { Button } from '@eriko/ui/src/components/ui/button';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { sendReply, draftReply } from '../../../api/conversations';

interface ReplyInputProps {
  orgId: string;
  conversationId: string;
  onSent: () => void;
}

export function ReplyInput({ orgId, conversationId, onSent }: ReplyInputProps) {
  const [value, setValue] = useState('');
  const [internalNote, setInternalNote] = useState(false);
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async () => {
    if (!value.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await sendReply(orgId, conversationId, {
        content: value,
        triggerAI: false,
        internalNote,
      });
      setValue('');
      setInternalNote(false);
      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const draft = async () => {
    if (drafting) return;
    setDrafting(true);
    setError(null);
    try {
      const res = await draftReply(orgId, conversationId);
      setValue(res.draft);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Draft failed');
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="border-t border-surface-200 px-4 py-3 bg-surface-0">
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Reply to the client…"
        rows={3}
        className="w-full resize-none rounded-md border border-surface-300 bg-surface-0 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-surface-500"
        disabled={sending}
      />
      <div className="flex items-center justify-between mt-2 gap-2">
        <label className="flex items-center gap-1.5 text-[11px] text-surface-600 cursor-pointer">
          <input
            type="checkbox"
            checked={internalNote}
            onChange={(e) => setInternalNote(e.target.checked)}
            className="h-3 w-3"
          />
          Internal note — only visible to reviewers
        </label>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={draft} disabled={drafting || sending}>
            <Sparkles className="w-3 h-3" />
            {drafting ? 'Drafting…' : 'Draft with AI'}
          </Button>
          <Button size="sm" onClick={send} disabled={!value.trim() || sending}>
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </div>
      {error && <p className="mt-2 text-[11px] text-neg">{error}</p>}
    </div>
  );
}
